import type {
  PlanRevisionCommentContextTelemetry,
  PlanRevisionFailureTelemetry,
  PlanRevisionIntentTelemetry,
  PlanRevisionRefineTelemetry,
  PlanRevisionStoryAttachmentTelemetry,
  PlanRevisionTelemetry,
} from "../types";

import { normalizeStringArray } from "../stringUtils";
import { sanitizePlanDiff, sanitizePlanTree } from "./plan";
import { sanitizeTouchpoints } from "./touchpoints";
import { sanitizeTracker } from "./tracker";
import { type UnknownRecord, isRecord } from "./utils";

export function sanitizeIntent(
  value: unknown,
): PlanRevisionIntentTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const intent: PlanRevisionIntentTelemetry = {};
  if (typeof record.should_proceed === "boolean") {
    intent.should_proceed = record.should_proceed;
  }
  if (
    record.scope === "story" ||
    record.scope === "subtasks" ||
    record.scope === "single_subtask"
  ) {
    intent.scope = record.scope;
  }
  if (typeof record.intent === "string") {
    intent.intent = record.intent;
  }
  if (typeof record.confidence === "number") {
    intent.confidence = record.confidence;
  }
  if (typeof record.notes === "string") {
    intent.notes = record.notes;
  }
  return Object.keys(intent).length > 0 ? intent : null;
}

export function sanitizeRefine(
  value: unknown,
): PlanRevisionRefineTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const refine: PlanRevisionRefineTelemetry = {};
  if ("planTree" in record) {
    const planTree = sanitizePlanTree(record.planTree);
    if (planTree) {
      refine.planTree = planTree;
    }
  }
  if ("diff" in record) {
    const diff = sanitizePlanDiff(record.diff);
    if (diff) {
      refine.diff = diff;
    }
  }
  if ("rawComments" in record) {
    const rawComments = normalizeStringArray(record.rawComments);
    if (rawComments) {
      refine.rawComments = rawComments;
    }
  }
  if ("summaries" in record) {
    const summaries = normalizeStringArray(record.summaries);
    if (summaries) {
      refine.summaries = summaries;
    }
  }
  return Object.keys(refine).length > 0 ? refine : null;
}

export function sanitizeTelemetry(
  value: unknown,
): PlanRevisionTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const telemetry: PlanRevisionTelemetry = {};
  const commentContextSource =
    "comment_context" in record
      ? record.comment_context
      : record.commentContext;
  const commentContext = sanitizeCommentContext(commentContextSource);
  if (commentContext) {
    telemetry.comment_context = commentContext;
  }
  const storyAttachmentsSource =
    "story_attachments" in record
      ? record.story_attachments
      : (record.storyAttachments ?? record.attachments);
  const storyAttachments = sanitizeStoryAttachments(storyAttachmentsSource);
  if (storyAttachments !== null) {
    telemetry.story_attachments = storyAttachments;
  }
  if ("intent" in record) {
    const intent = sanitizeIntent(record.intent);
    if (intent) {
      telemetry.intent = intent;
    }
  }
  if ("refine" in record) {
    const refine = sanitizeRefine(record.refine);
    if (refine) {
      telemetry.refine = refine;
    }
  }
  if ("touchpoints" in record) {
    const touchpoints = sanitizeTouchpoints(record.touchpoints);
    if (touchpoints) {
      telemetry.touchpoints = touchpoints;
    }
  }
  if ("tracker" in record) {
    const tracker = sanitizeTracker(record.tracker);
    if (tracker) {
      telemetry.tracker = tracker;
    }
  }
  if ("failure" in record) {
    const failure = sanitizeFailureTelemetry(record.failure);
    if (failure) {
      telemetry.failure = failure;
    }
  }
  if (typeof record.snapshot_conflict_count === "number") {
    telemetry.snapshot_conflict_count = record.snapshot_conflict_count;
  }
  if (typeof record.last_snapshot_conflict_at === "string") {
    telemetry.last_snapshot_conflict_at = record.last_snapshot_conflict_at;
  }
  return Object.keys(telemetry).length > 0 ? telemetry : null;
}

export function sanitizeStoryAttachments(
  value: unknown,
): PlanRevisionStoryAttachmentTelemetry[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const attachments: PlanRevisionStoryAttachmentTelemetry[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) {
      continue;
    }
    const id = readTrimmedString(entry, ["id"]);
    const filename = readTrimmedString(entry, ["filename", "name"]);
    const url = readTrimmedString(entry, ["url", "href"]);
    if (!id || !filename || !url) {
      continue;
    }
    const mimeType = readTrimmedString(entry, ["mimeType", "mime_type"]);
    const description = readTrimmedString(entry, ["description"]);
    const size =
      typeof entry.size === "number" && Number.isFinite(entry.size)
        ? entry.size
        : undefined;
    attachments.push({
      description: description ?? null,
      filename,
      id,
      mimeType: mimeType ?? null,
      size: typeof size === "number" ? size : null,
      url,
    });
  }
  return attachments;
}

export function sanitizeCommentContext(
  value: unknown,
): PlanRevisionCommentContextTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const rawBody = readTrimmedString(record, ["raw_body", "rawBody"]);
  const summary = readTrimmedString(record, ["summary"]);
  const context: PlanRevisionCommentContextTelemetry = {};
  if (rawBody) {
    context.raw_body = rawBody;
  }
  if (summary) {
    context.summary = summary;
  }
  return Object.keys(context).length > 0 ? context : null;
}

function readTrimmedString(
  record: UnknownRecord,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return undefined;
}

export function sanitizeFailureTelemetry(
  value: unknown,
): PlanRevisionFailureTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const reason = typeof record.reason === "string" ? record.reason.trim() : "";
  if (!reason) {
    return null;
  }
  const failure: PlanRevisionFailureTelemetry = { reason };
  if (typeof record.stage === "string" && record.stage.trim().length > 0) {
    failure.stage = record.stage.trim();
  }
  if (typeof record.status === "string" && record.status.trim().length > 0) {
    failure.status = record.status.trim();
  }
  if (
    typeof record.recordedAt === "string" &&
    record.recordedAt.trim().length > 0
  ) {
    failure.recordedAt = record.recordedAt;
  }
  return failure;
}
